import React from 'react';
import { createRoot } from 'react-dom/client';
import { QRCodeCanvas } from 'qrcode.react';
import { Product } from '../../../dataTypes/productManagment/types';
import { WWW_URLS } from '../../../backendServices/urls';

export interface AdvertisementCanvasProps {
  product?:         Product | null;
  selectedVariant?: number;
  selectedImage?:   number;
  width:            number;
  pixelRatio?:      number;
  onlyQRcode?:      boolean;
  comment1?:        string;
  comment2?:        string;
  shapeColor1?:     string;
  shapeColor2?:     string;
  backgroundColor1?: string;
  backgroundColor2?: string;
  redirectionUuid?: string;
  withImage?:       boolean;
  alternateImage?:  string | null;
}

const BASE_WIDTH = 320;

function htmlToLines(html: string): string[] {
  const text = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '');
  return text.split('\n').map(l => l.trim()).filter(Boolean);
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxW: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    const test = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(test).width <= maxW) {
      cur = test;
    } else {
      if (cur) lines.push(cur);
      cur = w;
    }
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : [''];
}

function measureBlock(
  ctx: CanvasRenderingContext2D,
  html: string,
  maxW: number,
  fontSize: number,
  lineH: number,
  bold: boolean
): { wrapped: string[][]; height: number } {
  ctx.font = `${bold ? 'bold ' : ''}${fontSize}px sans-serif`;
  const rawLines = htmlToLines(html);
  const wrapped = rawLines.map(l => wrapText(ctx, l, maxW));
  const totalLines = wrapped.reduce((n, ls) => n + ls.length, 0);
  return { wrapped, height: totalLines * lineH };
}

function drawBlock(
  ctx: CanvasRenderingContext2D,
  wrapped: string[][],
  cx: number,
  startY: number,
  fontSize: number,
  lineH: number,
  color: string,
  bold: boolean
): number {
  ctx.fillStyle = color;
  ctx.font = `${bold ? 'bold ' : ''}${fontSize}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  let y = startY;
  for (const group of wrapped) {
    for (const line of group) {
      ctx.fillText(line, cx, y);
      y += lineH;
    }
  }
  return y;
}

// Render QRCodeCanvas off-DOM at target resolution, return a pixel-exact snapshot.
// The container is hidden (position:fixed outside viewport) so it never appears on screen.
function renderQRCode(url: string, size: number, bgColor: string, fgColor: string): Promise<HTMLCanvasElement | null> {
  return new Promise((resolve) => {
    const host = document.createElement('div');
    host.style.cssText = 'position:fixed;top:-9999px;left:-9999px;visibility:hidden;pointer-events:none;';
    document.body.appendChild(host);

    let src: HTMLCanvasElement | null = null;
    const root = createRoot(host);
    root.render(
      React.createElement(QRCodeCanvas, {
        ref: (el: HTMLCanvasElement | null) => { src = el; },
        value: url,
        size,
        bgColor,
        fgColor,
      })
    );

    // Two rAFs guarantee the canvas has been painted by the browser
    requestAnimationFrame(() => requestAnimationFrame(() => {
      let snap: HTMLCanvasElement | null = null;
      if (src) {
        snap = document.createElement('canvas');
        snap.width = size;
        snap.height = size;
        const sc = snap.getContext('2d')!;
        sc.imageSmoothingEnabled = false;
        sc.drawImage(src, 0, 0);
        // QRCodeCanvas uses fractional fillRect when size/numModules isn't integer,
        // producing 1-pixel anti-aliased edges. Threshold to pure black/white.
        const img = sc.getImageData(0, 0, size, size);
        for (let i = 0; i < img.data.length; i += 4) {
          const v = (img.data[i] + img.data[i + 1] + img.data[i + 2]) / 3 < 128 ? 0 : 255;
          img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
          img.data[i + 3] = 255;
        }
        sc.putImageData(img, 0, 0);
      }
      root.unmount();
      document.body.removeChild(host);
      resolve(snap);
    }));
  });
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload  = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function canvasToFile(canvas: HTMLCanvasElement): Promise<File> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) { reject(new Error('canvas.toBlob failed')); return; }
      resolve(new File([blob], 'advertisement.png', { type: 'image/png' }));
    }, 'image/png');
  });
}

// Mirror of Advertisement.tsx URL logic
function resolveQRUrl(product: Product | null | undefined, redirectionUuid?: string): string {
  const qrChar = product?.characteristics?.find(c => c.kind === 'QRcode');
  const base = qrChar?.value ?? (
    product && product.code !== ''
      ? `${WWW_URLS.customer_front}/panier?product=${product.code}`
      : `${WWW_URLS.customer_front}`
  );
  return redirectionUuid
    ? `${WWW_URLS.customer_front}/partenaire?redirect=${redirectionUuid}`
    : base;
}

// Mirror of searchImage() in utils.ts
function resolveImageUrl(product: Product | null | undefined, selectedVariant: number, selectedImage: number): string | null {
  if (!product || product.uuid === '') return null;
  const variantCode = product.variants?.[selectedVariant]?.code ?? '';
  const cptStr = (selectedImage + 1).toString().padStart(4, '0');
  return `${WWW_URLS.MediaServer}${product.publicRef}/images/${variantCode}-${cptStr}_BIG.webp`;
}

/**
 * Build the Advertisement PNG entirely on an offscreen canvas at full target resolution.
 * Nothing is displayed on screen — the DOM is never used for layout measurement.
 */
export async function generateAdvertisementCanvas(props: AdvertisementCanvasProps): Promise<File> {
  const {
    product         = null,
    selectedVariant = 0,
    selectedImage   = 0,
    width,
    pixelRatio      = 4,
    onlyQRcode      = false,
    comment1        = 'Offre spéciale',
    comment2        = 'Scannez le QR code pour commander',
    shapeColor1     = '#FFF',
    shapeColor2     = '#000',
    backgroundColor1 = '#000',
    backgroundColor2 = '#ebff40',
    redirectionUuid,
    withImage       = true,
    alternateImage  = null,
  } = props;

  const s  = pixelRatio;
  const f  = (width / BASE_WIDTH) * s;
  const W  = Math.round(width * s);
  const cx = W / 2;

  const fontSize1 = Math.round(f * 40);
  const fontSize2 = Math.round(f * 32);
  const fontSize3 = Math.round(f * 28);
  const fontSize4 = Math.round(f * 32);
  const imageSize = Math.round(f * 280);   // QR + product image share this size
  const pad       = Math.round(0.02 * W);  // 2 % of scaled width
  const rem       = Math.round(16 * s);
  const halfRem   = Math.round(8 * s);

  const finalUrl = resolveQRUrl(product, redirectionUuid);
  const imgSrc   = withImage
    ? (alternateImage ?? resolveImageUrl(product, selectedVariant, selectedImage))
    : null;

  // Kick off async work in parallel
  const [qrCanvas, productImg] = await Promise.all([
    renderQRCode(finalUrl, imageSize, '#ffffff', '#000000'),
    imgSrc ? loadImage(imgSrc) : Promise.resolve(null),
  ]);

  // Use an offscreen canvas as a measurement context (no DOM layout needed)
  const mc = document.createElement('canvas');
  mc.width = W; mc.height = 1;
  const mctx = mc.getContext('2d')!;

  // ── onlyQRcode: trivial layout ──────────────────────────────────────────────
  if (onlyQRcode) {
    const H = imageSize + pad * 2;
    const canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);
    if (qrCanvas) {
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(qrCanvas, Math.round((W - imageSize) / 2), pad, imageSize, imageSize);
      ctx.imageSmoothingEnabled = true;
    }
    return canvasToFile(canvas);
  }

  // ── Full layout: measure each text block first ───────────────────────────────
  const lineH1 = Math.round(fontSize1 * 1.1);
  const lineH2 = Math.round(fontSize2 * 1.2);
  const lineH3 = Math.round(fontSize3 * 1.2);

  const { wrapped: c1Wrapped, height: c1TextH } = measureBlock(mctx, comment1,          W - pad * 2, fontSize1, lineH1, true);
  const { wrapped: c2Wrapped, height: c2TextH } = measureBlock(mctx, comment2,          W - pad * 4, fontSize2, lineH2, true);
  const { wrapped: nameWrapped, height: nameH  } = measureBlock(mctx, product?.name ?? '', W - pad * 4, fontSize3, lineH3, true);

  const price   = product?.variants?.[0]?.prices?.[0]?.priceWithTax ?? 0;
  const priceH  = price > 0 ? Math.round(fontSize4 * 1.2) + rem : 0;
  const imgBlkH = withImage ? imageSize + pad : 0;

  const block1H =
    pad + c1TextH + pad;

  const block2H =
    pad           +   // top padding of productContainer
    halfRem       +   // comment2 margin-top 0.5rem
    c2TextH       +   // comment2 text
    rem           +   // comment2 margin-bottom 1rem
    imgBlkH       +   // product image + 2% margin below (if any)
    halfRem       +   // name margin-top 0.5rem
    nameH         +   // product name
    halfRem       +   // name margin-bottom 0.5rem
    priceH        +   // price (if any)
    imageSize     +   // QR code
    rem           +   // QR margin-bottom 1rem
    pad;              // bottom padding

  // ── Draw ────────────────────────────────────────────────────────────────────
  const canvas = document.createElement('canvas');
  canvas.width  = W;
  canvas.height = Math.round(block1H + block2H);
  const ctx = canvas.getContext('2d')!;

  // Block 1 — comment1
  ctx.fillStyle = backgroundColor1;
  ctx.fillRect(0, 0, W, block1H);
  drawBlock(ctx, c1Wrapped, cx, pad, fontSize1, lineH1, shapeColor1, true);

  let y = block1H;

  // Block 2 — product container
  ctx.fillStyle = backgroundColor2;
  ctx.fillRect(0, y, W, block2H);

  y += pad + halfRem;
  y = drawBlock(ctx, c2Wrapped, cx, y, fontSize2, lineH2, shapeColor2, true);
  y += rem;

  if (withImage) {
    if (productImg) {
      const aspect = productImg.naturalWidth / productImg.naturalHeight;
      let dw = imageSize, dh = imageSize;
      if (aspect > 1) dh = imageSize / aspect;
      else             dw = imageSize * aspect;
      ctx.drawImage(productImg, (W - dw) / 2, y + (imageSize - dh) / 2, dw, dh);
    }
    y += imageSize + pad;
  }

  y += halfRem;
  y = drawBlock(ctx, nameWrapped, cx, y, fontSize3, lineH3, shapeColor2, true);
  y += halfRem;

  if (price > 0) {
    ctx.fillStyle = shapeColor2;
    ctx.font = `bold ${fontSize4}px sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(`${price.toFixed(2)} €`, cx, y);
    y += Math.round(fontSize4 * 1.2) + rem;
  }

  if (qrCanvas) {
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(qrCanvas, Math.round((W - imageSize) / 2), Math.round(y), imageSize, imageSize);
    ctx.imageSmoothingEnabled = true;
  }

  return canvasToFile(canvas);
}
